import type { BasketItem } from "@nwt/norce-pwa/dist/runtime/types/api/shopping/basket";
import type { StockStatusItem } from "~/types/storyblok/storyblok";

export type DeliveryTextView = "cart" | "checkout";

interface ResolveDeliveryTextArgs {
	item: Partial<BasketItem> | undefined;
	view: DeliveryTextView;
	statuses: StockStatusItem[];
	/**
	 * `useStockStatus().getStockStatus` — taken as an argument so this stays a
	 * pure function, the same shape as `formatParametricValue(value, t)`.
	 */
	resolveStatus: (idSeed?: string, inStock?: boolean) => StockStatusItem | null;
}

const textOf = (
	status: StockStatusItem | null | undefined,
	view: DeliveryTextView
): string =>
	(view === "checkout"
		? status?.checkoutDeliveryText
		: status?.cartDeliveryText
	)?.trim() || "";

/**
 * Delivery promise for one basket line, most-specific first. Step 1 exists
 * because `getStockStatus` sends an in-stock line through `fallbackInStockId`
 * — right for the button, wrong here: "Begagnat" ships differently.
 */
export const resolveDeliveryText = ({
	item,
	view,
	statuses,
	resolveStatus
}: ResolveDeliveryTextArgs): string => {
	if (!item) return "";

	// 1. The line's own flag.
	const flagIds =
		item.FlagIdSeed?.split(",")
			.map((id) => id.trim())
			.filter(Boolean) ?? [];

	for (const id of flagIds) {
		const text = textOf(
			statuses.find((status) => status.id === id),
			view
		);
		if (text) return text;
	}

	// 2. Whatever the stock status resolves to — the `fallbackInStockId` chain,
	//    then the generic `in-stock` / `out-of-stock` entries, which is where a
	//    site-wide default belongs. `OnHand.Value` is the stock the checkout sees.
	const onHand = item.OnHandValue ?? item.OnHand?.Value ?? 0;
	return textOf(resolveStatus(item.FlagIdSeed, onHand > 0), view);
};
